import { CONNECTION_TYPES, MCP_ADAPTERS, OAUTH_CALLBACK_PATH, type Connection, type ConnectionType, type McpAdapter } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Plus, X } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { CopyButton } from '@/components/code-block';
import { ConfigFieldsEditor, ConfigModeSwitch, configFromRows, rowsFromConfig, useConfigMode, type ConfigMode, type ConfigRow } from '@/components/config-fields';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { prettyJson } from '@/lib/format';
import { parseJsonObject, useApiMutation, useRuntimeProfiles } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { CONNECTION_TYPE, profileLabel } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

interface HeaderRow {
  key: string;
  value: string;
}

function headersFromConfig(config: Record<string, unknown> | undefined): HeaderRow[] {
  const h = config?.headers;
  if (!h || typeof h !== 'object' || Array.isArray(h)) return [];
  return Object.entries(h as Record<string, unknown>).map(([key, value]) => ({ key, value: String(value ?? '') }));
}

/** Config keys of an MCP connection that the dedicated form does not edit (e.g. authHeader/authScheme) — kept as-is. */
function mcpExtraConfig(config: Record<string, unknown> | undefined): Record<string, unknown> {
  const {
    url: _url,
    headers: _headers,
    adapter: _adapter,
    baseUrl: _baseUrl,
    projects: _projects,
    sites: _sites,
    properties: _properties,
    oauthClientId: _oauthClientId,
    namespaces: _namespaces,
    adAccounts: _adAccounts,
    runtimeProfileId: _runtimeProfileId,
    ...rest
  } = config ?? {};
  void [_url, _headers, _adapter, _baseUrl, _projects, _sites, _properties, _oauthClientId, _namespaces, _adAccounts, _runtimeProfileId];
  return Object.fromEntries(Object.entries(rest).filter(([key]) => !SETTING_KEYS.has(key)));
}

interface AdapterForm {
  label: string;
  /** The name a new connection gets. */
  name: string;
  /** A hosted API at a fixed address: no URL asked. */
  hosted?: boolean;
  /** The instance's address, asked when not hosted. */
  address?: { label: string; hint: string; placeholder: string };
  /** Signs in with an access token or with OAuth through an application registered on the instance (GitLab). */
  oauth?: boolean;
  /** No key at all (Deskworks's own SearXNG): nothing to fill in. */
  keyless?: boolean;
  /** The config list that limits what the tools reach. */
  scope?: { config: string; label: string; hint: string; placeholder: string };
  /** Signs in only with OAuth through the customer's Google OAuth client: the APIs to enable in its project. */
  google?: string[];
  /** A hosted API's key: its label and where to get it. */
  credential?: { label: string; hint: string };
  /** Text settings the tools use (`config.<key>`), e.g. an image model or a default location. */
  settings?: { config: string; label: string; placeholder: string; hint: string }[];
  /** The credential is a login and a password (HTTP Basic), saved as `login:password`. */
  basic?: { login: string; password: string; hint: string };
  /**
   * The access is pasted as one JSON object (Kubernetes): Deskworks keeps the `secret` field encrypted and the
   * `config` fields (JSON field → config key) as the connection's config, shown again when editing.
   */
  json?: { label: string; hint: string; placeholder: string; secret: string; required: string[]; config: Record<string, string>; otherSecrets?: string[] };
  /**
   * The access can come from a runtime of these types (`config.runtimeProfileId`) instead of the connection's own
   * `credential` (ChatGPT, docs/features/chatgpt.md).
   */
  runtimeAccess?: { types: string[]; hint: string };
}

/** Types of their own in the dialog, saved as an MCP server connection with that adapter. */
const ADAPTER_TYPES = ['kubernetes', 'hetzner_cloud', 'aws'] as const;
type AdapterType = (typeof ADAPTER_TYPES)[number];
const isAdapterType = (v: string): v is AdapterType => (ADAPTER_TYPES as readonly string[]).includes(v);

const ADAPTER_FORM: Record<McpAdapter, AdapterForm> = {
  gitlab: {
    label: t('GitLab (built-in, REST API)'),
    name: 'GitLab',
    address: { label: t('GitLab URL'), hint: t('The address of your GitLab, e.g. https://gitlab.com or https://gitlab.example.com.'), placeholder: 'https://gitlab.com' },
    oauth: true,
    scope: {
      config: 'projects',
      label: t('Projects'),
      hint: t('Comma-separated project paths (group/app). Agents only reach these; empty = everything the token can see.'),
      placeholder: 'group/app, group/gitops',
    },
  },
  web_search: { label: t('Web search (built-in, free)'), name: 'Web search', hosted: true, keyless: true },
  google_search: {
    label: t('Google Search (built-in, via Serper, paid)'),
    name: 'Google Search',
    hosted: true,
    credential: {
      label: t('Serper API key'),
      hint: t('Google results come through Serper (serper.dev): create an account there and paste its API key. Every search uses one credit. Stored encrypted and never shown again.'),
    },
  },
  chatgpt: {
    label: t('ChatGPT (built-in: ask, research, images)'),
    name: 'ChatGPT',
    hosted: true,
    runtimeAccess: {
      types: ['codex_subscription', 'openai_api'],
      hint: t('A ChatGPT subscription runtime uses its login and plan (pictures need a paid plan); an OpenAI API runtime or an own API key is billed per call. Agents never see either.'),
    },
    credential: {
      label: t('OpenAI API key'),
      hint: t('An API key from platform.openai.com. Every call is billed by OpenAI. Stored encrypted and never shown again.'),
    },
    settings: [
      { config: 'model', label: t('Text model'), placeholder: 'gpt-5', hint: t('With an API key: the model for ask_chatgpt and research. Empty = the runtime’s model, else gpt-5. With a subscription: empty = the plan’s model.') },
      { config: 'imageModel', label: t('Image model'), placeholder: 'gpt-image-2', hint: t('With an API key: the OpenAI image model. Empty = gpt-image-2.') },
    ],
  },
  dataforseo: {
    label: t('DataForSEO (built-in, whole API, paid)'),
    name: 'DataForSEO',
    hosted: true,
    basic: {
      login: t('API login'),
      password: t('API password'),
      hint: t('From app.dataforseo.com → API Access (not the password of the account). Every call is paid from the DataForSEO balance. Stored encrypted and never shown again.'),
    },
    settings: [
      {
        config: 'location',
        label: t('Default location'),
        placeholder: 'Germany',
        hint: t('Used when an agent names no location: a name (Germany, Berlin,Germany) or a location code (2276). Empty = each endpoint’s own default.'),
      },
      { config: 'language', label: t('Default language'), placeholder: 'de', hint: t('Used when an agent names no language: a code (de) or a name (German).') },
    ],
  },
  kubernetes: {
    label: t('Kubernetes (built-in)'),
    name: 'Kubernetes',
    json: {
      label: t('Cluster access (JSON)'),
      hint: t(
        'A ServiceAccount token, or a kubeconfig user\'s "client-certificate-data" and "client-key-data" instead: what agents may do in the cluster is set by its RBAC. "caData" is the cluster\'s CA ("certificate-authority-data" of the kubeconfig), needed when the API server\'s certificate isn\'t from a public CA (k3s, kubeadm). "namespaces" (optional) limits the tools to these. The token is stored encrypted and never shown again.',
      ),
      placeholder: '{\n  "server": "https://k8s.example.com:6443",\n  "token": "eyJhbGciOi…",\n  "caData": "LS0tLS1CRUdJTi…",\n  "namespaces": ["default"]\n}',
      secret: 'token',
      // A client certificate's key instead of the token (the certificate goes into the config, not shown again).
      otherSecrets: ['client-key-data', 'clientKeyData'],
      required: ['server'],
      config: { server: 'baseUrl', caData: 'caData', namespaces: 'namespaces' },
    },
  },
  grafana: {
    label: t('Grafana (built-in)'),
    name: 'Grafana',
    address: { label: t('Grafana URL'), hint: t('The address of your Grafana, e.g. https://grafana.example.com or https://example.grafana.net.'), placeholder: 'https://grafana.example.com' },
    credential: {
      label: t('Service account token'),
      hint: t(
        'Administration → Users and access → Service accounts → Add service account, then Add service account token. The role decides what agents may do: Viewer reads dashboards, queries data sources and sees alerts; Editor also adds annotations and silences alerts. Stored encrypted and never shown again.',
      ),
    },
  },
  hetzner_cloud: {
    label: t('Hetzner Cloud (built-in)'),
    name: 'Hetzner Cloud',
    hosted: true,
    credential: {
      label: t('API token'),
      hint: t(
        'Cloud Console → your project → Security → API tokens → Generate API token, with Read & Write (Read only: agents only look). A token reaches only its own project: one connection per project. Projects themselves are created in the Console. Stored encrypted and never shown again.',
      ),
    },
  },
  aws: {
    label: t('AWS (built-in)'),
    name: 'AWS',
    // No address: the pasted access carries the key and the region.
    json: {
      label: t('Access key (JSON)'),
      hint: t(
        'IAM → Users → Create user (e.g. "deskworks", no console access), attach a policy (ReadOnlyAccess to look; more to create), then Security credentials → Create access key. What agents may do is that policy. The secret access key is stored encrypted and never shown again.',
      ),
      placeholder: '{\n  "accessKeyId": "AKIA…",\n  "secretAccessKey": "…",\n  "region": "eu-central-1"\n}',
      secret: 'secretAccessKey',
      required: ['accessKeyId', 'region'],
      config: { accessKeyId: 'accessKeyId', region: 'region' },
    },
  },
  meta_ads: {
    label: t('Meta Ads (built-in, Marketing API)'),
    name: 'Meta Ads',
    hosted: true,
    scope: {
      config: 'adAccounts',
      label: t('Ad accounts'),
      hint: t('Comma-separated ad account IDs (act_123…). Agents only reach these and their campaigns, ad sets and ads; empty = every account the token reaches.'),
      placeholder: 'act_778260454843923',
    },
    credential: {
      label: t('System user access token'),
      hint: t(
        'Business settings → Users → System users → Generate token, for an app with the Marketing API, with ads_read and ads_management (to create and change ads) and the ad account assigned. Choose “never expires”. Everything agents create starts paused. Stored encrypted and never shown again.',
      ),
    },
  },
  google_search_console: {
    label: t('Google Search Console (built-in)'),
    name: 'Google Search Console',
    hosted: true,
    scope: {
      config: 'sites',
      label: t('Properties'),
      hint: t('Comma-separated, as Search Console names them (https://www.example.com/ or sc-domain:example.com). Agents only reach these; empty = every property of the Google account.'),
      placeholder: 'sc-domain:example.com',
    },
    google: ['Google Search Console API'],
  },
  google_analytics: {
    label: t('Google Analytics 4 (built-in)'),
    name: 'Google Analytics',
    hosted: true,
    scope: {
      config: 'properties',
      label: t('Properties'),
      hint: t('Comma-separated GA4 property IDs (Admin → Property details). Agents only reach these; empty = every property of the Google account.'),
      placeholder: '123456789',
    },
    google: ['Google Analytics Admin API', 'Google Analytics Data API'],
  },
};

/** Config keys the adapter forms edit as settings: not carried over as extra config. */
const SETTING_KEYS = new Set(Object.values(ADAPTER_FORM).flatMap((f) => (f.settings ?? []).map((s) => s.config)));

function isHttpUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Create (no `connection`) or edit a Connection. Credentials are write-only. */
export function ConnectionDialog({
  workspaceId,
  connection,
  children,
}: {
  workspaceId: string;
  connection?: Connection;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ConnectionType>('http_api');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [config, setConfig] = useState('');
  const [configTouched, setConfigTouched] = useState(false);
  /** The configuration is edited either as rows (`rows`) or as JSON text (`config`), whichever mode is on. */
  const [mode, setMode] = useConfigMode();
  const [rows, setRows] = useState<ConfigRow[]>([]);
  const [credential, setCredential] = useState('');
  const [mcpUrl, setMcpUrl] = useState('');
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  /** An MCP connection is either a remote server (`url`) or one of Deskworks's own adapters (`adapter` + `baseUrl`). */
  const [adapter, setAdapter] = useState<McpAdapter | ''>('');
  const [baseUrl, setBaseUrl] = useState('');
  /** The adapter's scope list (GitLab projects, Search Console sites, GA4 properties), comma-separated. */
  const [scopeList, setScopeList] = useState('');
  /** An adapter signs in with an access token or with OAuth through an application registered on the instance (`oauthClientId`). */
  const [adapterAuth, setAdapterAuth] = useState<'token' | 'oauth'>('token');
  const [oauthClientId, setOauthClientId] = useState('');
  /** A Google adapter signs in through the customer's own OAuth client instead of Deskworks's. */
  const [ownGoogleClient, setOwnGoogleClient] = useState(false);
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: Infinity, retry: false, enabled: open });
  const [settings, setSettings] = useState<Record<string, string>>({});
  /** An adapter whose credential is a login and a password (`basic`). */
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  /** The access of a `json` adapter as pasted; when editing, its config fields without the secret. */
  const [access, setAccess] = useState('');
  /** A `runtimeAccess` adapter: the runtime it takes its access from; '' = the connection's own key. */
  const [runtimeId, setRuntimeId] = useState('');

  useEffect(() => {
    if (!open) return;
    const initial = connection?.type ?? 'http_api';
    setType(initial);
    setName(connection?.name ?? '');
    setDescription(connection?.description ?? '');
    setConfig(prettyJson(connection ? connection.config : CONNECTION_TYPE[initial].configTemplate));
    setRows(rowsFromConfig(connection ? connection.config : CONNECTION_TYPE[initial].configTemplate));
    setConfigTouched(false);
    setCredential('');
    setMcpUrl(typeof connection?.config.url === 'string' ? connection.config.url : '');
    setHeaders(headersFromConfig(connection?.config));
    const a = connection?.config.adapter;
    setAdapter(typeof a === 'string' && (MCP_ADAPTERS as readonly string[]).includes(a) ? (a as McpAdapter) : '');
    setBaseUrl(typeof connection?.config.baseUrl === 'string' ? connection.config.baseUrl : '');
    const listed = typeof a === 'string' ? connection?.config[ADAPTER_FORM[a as McpAdapter]?.scope?.config ?? ''] : undefined;
    setScopeList(Array.isArray(listed) ? listed.join(', ') : '');
    const clientId = typeof connection?.config.oauthClientId === 'string' ? connection.config.oauthClientId : '';
    setOauthClientId(clientId);
    setAdapterAuth(clientId ? 'oauth' : 'token');
    setOwnGoogleClient(!!clientId);
    setSettings(Object.fromEntries(Object.entries(connection?.config ?? {}).filter(([k, v]) => SETTING_KEYS.has(k) && typeof v === 'string')) as Record<string, string>);
    setLogin('');
    setPassword('');
    const jsonForm = typeof a === 'string' ? ADAPTER_FORM[a as McpAdapter]?.json : undefined;
    const shown = jsonForm && connection ? Object.entries(jsonForm.config).filter(([, key]) => connection.config[key] !== undefined) : [];
    setAccess(shown.length ? prettyJson(Object.fromEntries(shown.map(([field, key]) => [field, connection!.config[key]]))) : '');
    setRuntimeId(typeof connection?.config.runtimeProfileId === 'string' ? connection.config.runtimeProfileId : '');
  }, [open, connection]);

  const onTypeChange = (next: ConnectionType | AdapterType) => {
    // GitLab, Kubernetes, Hetzner Cloud and AWS are reached through their built-in MCP adapters: an MCP server connection with that adapter chosen.
    if (next === 'gitlab' || isAdapterType(next)) {
      setType('mcp_server');
      setAdapter(next);
      if (!name.trim()) setName(ADAPTER_FORM[next].name);
      return;
    }
    if (adapter && isAdapterType(adapter)) setAdapter('');
    setType(next);
    if (!configTouched && !connection) {
      setConfig(prettyJson(CONNECTION_TYPE[next].configTemplate));
      setRows(rowsFromConfig(CONNECTION_TYPE[next].configTemplate));
    }
  };

  const isMcp = type === 'mcp_server';
  const parsed = mode === 'json' ? parseJsonObject(config) : configFromRows(rows);
  const configError = isMcp ? null : 'error' in parsed ? parsed.error : null;

  /** Carries the configuration over to the other mode; stays put while the current one has an error (it is shown). */
  const switchMode = (next: ConfigMode) => {
    if (next === mode || 'error' in parsed) return;
    if (next === 'json') setConfig(prettyJson(parsed.value));
    else setRows(rowsFromConfig(parsed.value));
    setMode(next);
  };
  const form = adapter ? ADAPTER_FORM[adapter] : null;
  const hosted = !!form?.hosted;
  const keyless = !!form?.keyless;
  const google = !!form?.google;
  /** Deskworks signs in with its own Google OAuth client: nothing to set up, only to sign in. */
  const deskworksGoogle = google && !!health.data?.googleOAuth && !ownGoogleClient;
  const json = form?.json;
  const runtimeAccess = form?.runtimeAccess;
  const runtimes = useRuntimeProfiles(open && runtimeAccess ? workspaceId : '');
  const accessRuntimes = (runtimes.data ?? []).filter((p) => runtimeAccess?.types.includes(p.runtimeType));
  // The access is a runtime's: no key of the connection's own is asked for.
  const viaRuntime = !!runtimeAccess && !!runtimeId;
  const accessError = json ? jsonAccessError(access, json, !connection?.hasCredential) : null;
  const serverUrl = adapter ? baseUrl : mcpUrl;
  const urlError = isMcp && serverUrl.trim() && !isHttpUrl(serverUrl.trim()) ? t('Please enter a complete URL starting with http:// or https://.') : null;
  const useOAuth = isMcp && !!adapter && (google || (!hosted && !!form?.oauth && adapterAuth === 'oauth'));
  const invalid =
    !name.trim() ||
    !!configError ||
    (isMcp && !hosted && !json && (!serverUrl.trim() || !!urlError)) ||
    (isMcp && !!json && !!accessError) ||
    (useOAuth && !deskworksGoogle && !oauthClientId.trim()) ||
    (isMcp && google && !deskworksGoogle && !credential && !connection?.hasCredential) ||
    (!!runtimeAccess && !runtimeId && !credential && !connection?.hasCredential) ||
    (!!form?.basic && (!!login.trim() !== !!password || (!connection?.hasCredential && !login.trim())));
  const redirectUri = `${window.location.origin}${OAUTH_CALLBACK_PATH}`;

  const buildConfig = (): Record<string, unknown> => {
    if (!isMcp) return 'value' in parsed ? parsed.value : {};
    if (adapter) {
      const list = form?.scope ? scopeList.split(/[\s,]+/).filter(Boolean) : [];
      // The pasted access carries the address; the Control Plane moves it into the config.
      if (json) return { ...mcpExtraConfig(connection?.config), adapter };
      return {
        ...mcpExtraConfig(connection?.config),
        adapter,
        ...(hosted ? {} : { baseUrl: baseUrl.trim() }),
        ...(list.length ? { [form!.scope!.config]: list } : {}),
        ...(useOAuth && !deskworksGoogle ? { oauthClientId: oauthClientId.trim() } : {}),
        ...(viaRuntime ? { runtimeProfileId: runtimeId } : {}),
        ...Object.fromEntries((form?.settings ?? []).map((s) => [s.config, (settings[s.config] ?? '').trim()]).filter(([, v]) => v)),
      };
    }
    const hdrs = Object.fromEntries(headers.filter((h) => h.key.trim()).map((h) => [h.key.trim(), h.value]));
    return {
      ...mcpExtraConfig(connection?.config),
      url: mcpUrl.trim(),
      ...(Object.keys(hdrs).length ? { headers: hdrs } : {}),
    };
  };

  const save = useApiMutation(
    () => {
      const cfg = buildConfig();
      const cred = json
        ? access.trim()
        : form?.basic
          ? login.trim() && password
            ? `${login.trim()}:${password}`
            : undefined
          : credential
            ? credential
            : undefined;
      return connection
        ? api.connections.update(connection.id, { name: name.trim(), description: description.trim(), config: cfg, credential: cred })
        : api.connections.create(workspaceId, { type, name: name.trim(), description: description.trim() || undefined, config: cfg, credential: cred });
    },
    {
      success: connection ? t('Connection saved') : t('Connection created'),
      invalidate: (c) => [qk.connections(workspaceId), qk.connection(c.id), qk.overview(workspaceId)],
      onSuccess: (c) => {
        setOpen(false);
        setCredential('');
        if (!connection) void navigate({ to: '/w/$workspaceId/connections/$connectionId', params: { workspaceId, connectionId: c.id } });
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!invalid) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={onSubmit} className="grid gap-4" autoComplete="off">
          <DialogHeader>
            <DialogTitle>{connection ? t('Edit connection') : t('New connection')}</DialogTitle>
            <DialogDescription>
              {t('A connection links Deskworks to an external system (e.g. a ticket system, email, GitLab). Agents only reach it through Deskworks — they never see the credentials.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Type')}>
              <Select value={type === 'mcp_server' && adapter && isAdapterType(adapter) ? adapter : type} onValueChange={(v) => onTypeChange(v as ConnectionType | AdapterType)} disabled={!!connection}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONNECTION_TYPES.map((ct) => (
                    <SelectItem key={ct} value={ct}>
                      {CONNECTION_TYPE[ct].label}
                    </SelectItem>
                  ))}
                  {ADAPTER_TYPES.map((a) => (
                    <SelectItem key={a} value={a}>
                      {ADAPTER_FORM[a].name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('Name')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Ticket system')} required autoFocus />
            </Field>
          </div>
          <Field label={t('Description')} optional>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('What do agents use this connection for?')} />
          </Field>
          {isMcp ? (
            <>
              <Field label={t('Server')} hint={adapter ? t('Deskworks serves this system’s API as MCP tools itself — nothing to host.') : undefined}>
                <Select
                  value={adapter || 'remote'}
                  onValueChange={(v) => {
                    setAdapter(v === 'remote' ? '' : (v as McpAdapter));
                    if (v !== 'remote' && (!name.trim() || Object.values(ADAPTER_FORM).some((f) => f.name === name.trim()))) setName(ADAPTER_FORM[v as McpAdapter].name);
                  }}
                  disabled={!!connection}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="remote">{t('MCP server (URL)')}</SelectItem>
                    {MCP_ADAPTERS.map((a) => (
                      <SelectItem key={a} value={a}>
                        {ADAPTER_FORM[a].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {google ? (
                <GoogleOAuthFields
                  deskworks={deskworksGoogle}
                  onOwnClient={health.data?.googleOAuth ? setOwnGoogleClient : undefined}
                  apis={form!.google!}
                  redirectUri={redirectUri}
                  clientId={oauthClientId}
                  onClientId={setOauthClientId}
                  secret={credential}
                  onSecret={setCredential}
                  hasSecret={!!connection?.hasCredential}
                />
              ) : hosted ? null : json ? (
                <Field
                  label={json.label}
                  error={access.trim() ? accessError : null}
                  hint={connection?.hasCredential ? t('The token is saved and not shown. Leave "{field}" out to keep it — or add it to replace it.', { field: json.secret }) : json.hint}
                >
                  <Textarea
                    value={access}
                    onChange={(e) => setAccess(e.target.value)}
                    rows={6}
                    placeholder={json.placeholder}
                    aria-label={json.label}
                    className="font-mono text-[13px]"
                    spellCheck={false}
                    autoComplete="off"
                  />
                </Field>
              ) : adapter ? (
                <>
                  <Field label={form!.address!.label} error={urlError} hint={form!.address!.hint}>
                    <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder={form!.address!.placeholder} required spellCheck={false} />
                  </Field>
                  {form?.oauth && (
                    <Field
                      label={t('Sign-in')}
                      hint={
                        useOAuth
                          ? t('Deskworks acts as the person who signs in on the connection page after saving, and keeps the access fresh on its own.')
                          : undefined
                      }
                    >
                      <Select value={adapterAuth} onValueChange={(v) => setAdapterAuth(v as 'token' | 'oauth')}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="token">{t('Access token')}</SelectItem>
                          <SelectItem value="oauth">{t('OAuth (GitLab application)')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  {useOAuth && (
                    <>
                      <Field label={t('Application ID')} hint={t('From the GitLab application (Admin area, group or user settings → Applications).')}>
                        <Input value={oauthClientId} onChange={(e) => setOauthClientId(e.target.value)} required spellCheck={false} className="font-mono text-[13px]" />
                      </Field>
                      <div className="grid gap-1.5 rounded-md border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
                        <p>
                          {tr('Create the application in GitLab with the scope {api}, {confidential} unchecked (Deskworks signs in with PKCE, no secret), and this redirect URI:', {
                            api: <code className="font-mono">api</code>,
                            confidential: <strong>Confidential</strong>,
                          })}
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 font-mono text-[12px] text-foreground">{redirectUri}</code>
                          <CopyButton value={redirectUri} />
                        </div>
                        <p>{t('Every address Deskworks is opened at (e.g. dev and live) needs its own line in the redirect URIs.')}</p>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Field label={t('MCP URL')} error={urlError} hint={t('The address of the MCP server (Streamable HTTP), e.g. https://example.com/mcp.')}>
                    <Input value={mcpUrl} onChange={(e) => setMcpUrl(e.target.value)} placeholder="https://example.com/mcp" required spellCheck={false} />
                  </Field>
                  <HeadersEditor rows={headers} onChange={setHeaders} />
                </>
              )}
            {runtimeAccess && (
              <Field label={t('Access')} hint={runtimeAccess.hint}>
                <Select value={runtimeId || 'own'} onValueChange={(v) => setRuntimeId(v === 'own' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {accessRuntimes.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {t('Runtime: {name}', { name: profileLabel(p) })}
                      </SelectItem>
                    ))}
                    <SelectItem value="own">{form?.credential?.label ?? t('Own key')}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
            {/* Google: the properties are ticked on the connection page after signing in, from what the account sees. */}
            {form?.scope && !google && (
              <Field label={form.scope.label} optional hint={form.scope.hint}>
                <Input value={scopeList} onChange={(e) => setScopeList(e.target.value)} placeholder={form.scope.placeholder} spellCheck={false} />
              </Field>
            )}
            {form?.settings?.map((s) => (
              <Field key={s.config} label={s.label} optional hint={s.hint}>
                <Input
                  value={settings[s.config] ?? ''}
                  onChange={(e) => setSettings((prev) => ({ ...prev, [s.config]: e.target.value }))}
                  placeholder={s.placeholder}
                  spellCheck={false}
                />
              </Field>
            ))}
            {form?.basic && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={form.basic.login}>
                  <Input
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    autoComplete="off"
                    placeholder={connection?.hasCredential ? t('(unchanged)') : ''}
                    spellCheck={false}
                  />
                </Field>
                <Field label={form.basic.password}>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder={connection?.hasCredential ? t('•••••••• (unchanged)') : ''}
                  />
                </Field>
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  {connection?.hasCredential ? t('Login and password are saved. Leave both empty to keep them — or enter new ones to replace them.') : form.basic.hint}
                </p>
              </div>
            )}
            {keyless && (
              <p className="rounded-md border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
                {t('Free and without a key: Deskworks searches with its own SearXNG, which asks Google, Bing, DuckDuckGo and others at once. Give the connection to the desks that should search.')}
              </p>
            )}
            {!useOAuth && !keyless && !form?.basic && !json && !viaRuntime && (
            <Field
              label={form?.credential?.label ?? (adapter ? t('Access token') : t('Token (sent as Bearer)'))}
              optional={!adapter}
              hint={
                connection
                  ? connection.hasCredential
                    ? t('Credentials are saved. Leave empty to keep them — or enter new ones to replace them.')
                    : t('No credentials saved yet.')
                  : form?.credential
                    ? form.credential.hint
                    : adapter
                    ? t('A personal, project or group access token with the api scope (read_api is enough for read-only desks). Stored encrypted and never shown again.')
                    : t('Sent to the MCP server as “Authorization: Bearer …”, stored encrypted and never shown again. Leave empty if the server has an OAuth sign-in: you sign in on the connection page after saving.')
              }
            >
              <Input
                type="password"
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                autoComplete="new-password"
                placeholder={connection?.hasCredential ? t('•••••••• (unchanged)') : ''}
              />
            </Field>
            )}
            </>
          ) : (
            <>
            <div className="grid gap-1.5">
              <div className="flex items-center justify-between gap-3">
                <Label>{t('Configuration')}</Label>
                <ConfigModeSwitch value={mode} onChange={switchMode} />
              </div>
              {mode === 'json' ? (
                <Textarea
                  value={config}
                  onChange={(e) => {
                    setConfig(e.target.value);
                    setConfigTouched(true);
                  }}
                  rows={6}
                  aria-label={t('Configuration (JSON)')}
                  aria-invalid={configError ? true : undefined}
                  className="font-mono text-[13px]"
                  spellCheck={false}
                />
              ) : (
                <ConfigFieldsEditor
                  type={type}
                  rows={rows}
                  onChange={(next) => {
                    setRows(next);
                    setConfigTouched(true);
                  }}
                />
              )}
              {configError ? (
                <p className="text-xs text-destructive">{configError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {type === 'http_api' ? (
                    tr('For HTTP APIs: {baseUrl} and the header the credentials go into ({authHeader}).', {
                      baseUrl: <code className="font-mono">baseUrl</code>,
                      authHeader: <code className="font-mono">authHeader</code>,
                    })
                  ) : type === 'git_repository' ? (
                    tr('The https clone {url}. Agents clone through Deskworks and may only push branches starting with {prefix} (default {fallback}). The access token goes in the field below.', {
                      url: <code className="font-mono">url</code>,
                      prefix: <code className="font-mono">pushBranchPrefix</code>,
                      fallback: <code className="font-mono">deskworks/</code>,
                    })
                  ) : (
                    t('Technical settings without secrets. Passwords and tokens go in the field below.')
                  )}
                </p>
              )}
            </div>
            <Field
              label={t('Credentials')}
              optional
              hint={
                connection
                  ? connection.hasCredential
                    ? t('Credentials are saved. Leave empty to keep them — or enter new ones to replace them.')
                    : t('No credentials saved yet.')
                  : t('Token, API key or password. Stored encrypted and never shown again.')
              }
            >
              <Input
                type="password"
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
                autoComplete="new-password"
                placeholder={connection?.hasCredential ? t('•••••••• (unchanged)') : ''}
              />
            </Field>
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={invalid}>
              {connection ? t('Save') : t('Create connection')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** What is wrong with a pasted JSON access, or null. A new connection needs its secret; the Control Plane checks the rest. */
function jsonAccessError(text: string, json: NonNullable<AdapterForm['json']>, needsSecret: boolean): string | null {
  if (!text.trim()) return t('Paste the access as JSON.');
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return t('This is not valid JSON.');
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return t('The access must be a JSON object { … }.');
  const fields = value as Record<string, unknown>;
  const given = (k: string) => typeof fields[k] === 'string' && !!String(fields[k]).trim();
  const secretGiven = [json.secret, ...(json.otherSecrets ?? [])].some(given);
  const missing = [...json.required, ...(needsSecret && !secretGiven ? [json.secret] : [])].filter((k) => !given(k));
  if (missing.length) return t('Missing: {fields}.', { fields: missing.map((k) => `"${k}"`).join(', ') });
  const address = Object.entries(json.config).find(([, key]) => key === 'baseUrl')?.[0];
  if (address && !String(fields[address] ?? '').startsWith('https://')) return t('"{field}" must be an https:// address.', { field: address });
  return null;
}

/** A Google adapter signs in through the customer's own Google OAuth client: how to create it, its ID and secret. */
function GoogleOAuthFields({
  deskworks,
  onOwnClient,
  apis,
  redirectUri,
  clientId,
  onClientId,
  secret,
  onSecret,
  hasSecret,
}: {
  deskworks: boolean;
  /** Deskworks has its own Google OAuth client: switches between it and the customer's own one. */
  onOwnClient?: (own: boolean) => void;
  apis: string[];
  redirectUri: string;
  clientId: string;
  onClientId: (v: string) => void;
  secret: string;
  onSecret: (v: string) => void;
  hasSecret: boolean;
}) {
  if (deskworks) {
    return (
      <div className="grid gap-1.5 rounded-md border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
        <p>
          {t('Nothing to set up at Google: after saving, sign in with Google on the connection page, with an account that can see your properties, and tick the ones agents may use. Deskworks only reads, and keeps the access fresh on its own.')}
        </p>
        <button type="button" onClick={() => onOwnClient?.(true)} className="justify-self-start text-foreground underline underline-offset-2 hover:no-underline">
          {t('Use an OAuth client of your own Google Cloud project instead')}
        </button>
      </div>
    );
  }
  return (
    <>
      <div className="grid gap-1.5 rounded-md border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
        <p>{t('Deskworks signs in with an OAuth client of your Google Cloud project (console.cloud.google.com):')}</p>
        <ol className="grid list-decimal gap-1 pl-4">
          <li>
            {tr('APIs & Services → Library: enable {apis}.', {
              apis: apis.map((api, i) => (
                <span key={api}>
                  {i > 0 && ` ${t('and')} `}
                  <strong>{api}</strong>
                </span>
              )),
            })}
          </li>
          <li>
            {tr('Google Auth Platform: set up the consent screen. {internal} for a Google Workspace; External apps stay signed in only for 7 days while in {testing}, so publish them.', {
              internal: <strong>{t('Internal')}</strong>,
              testing: <em>{t('Testing')}</em>,
            })}
          </li>
          <li>
            {tr('Clients → Create client → {web}, with this authorized redirect URI (one per address Deskworks is opened at):', {
              web: <strong>{t('Web application')}</strong>,
            })}
          </li>
        </ol>
        <div className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 font-mono text-[12px] text-foreground">{redirectUri}</code>
          <CopyButton value={redirectUri} />
        </div>
        <p>{t('Then paste the client ID and secret here. After saving, sign in with Google on the connection page and tick the properties agents may use.')}</p>
        {onOwnClient && (
          <button type="button" onClick={() => onOwnClient(false)} className="justify-self-start text-foreground underline underline-offset-2 hover:no-underline">
            {t('Sign in through Deskworks instead, without a client of your own')}
          </button>
        )}
      </div>
      <Field label={t('Client ID')}>
        <Input
          value={clientId}
          onChange={(e) => onClientId(e.target.value)}
          placeholder="…apps.googleusercontent.com"
          required
          spellCheck={false}
          className="font-mono text-[13px]"
        />
      </Field>
      <Field
        label={t('Client secret')}
        hint={hasSecret ? t('The client secret is saved. Leave empty to keep it — or enter a new one to replace it.') : t('Stored encrypted and never shown again.')}
      >
        <Input
          type="password"
          value={secret}
          onChange={(e) => onSecret(e.target.value)}
          autoComplete="new-password"
          placeholder={hasSecret ? t('•••••••• (unchanged)') : 'GOCSPX-…'}
          required={!hasSecret}
        />
      </Field>
    </>
  );
}

function HeadersEditor({ rows, onChange }: { rows: HeaderRow[]; onChange: (rows: HeaderRow[]) => void }) {
  const update = (i: number, patch: Partial<HeaderRow>) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="grid gap-1.5">
      <Label>
        {t('Additional headers')} <span className="font-normal text-muted-foreground">{t('(optional)')}</span>
      </Label>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <Input value={r.key} onChange={(e) => update(i, { key: e.target.value })} placeholder={t('Header name')} className="flex-1 font-mono text-[13px]" spellCheck={false} />
          <Input value={r.value} onChange={(e) => update(i, { value: e.target.value })} placeholder={t('Value')} className="flex-1 font-mono text-[13px]" spellCheck={false} />
          <Button type="button" variant="ghost" size="icon" aria-label={t('Remove header')} onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
            <X />
          </Button>
        </div>
      ))}
      <div>
        <Button type="button" variant="outline" size="xs" onClick={() => onChange([...rows, { key: '', value: '' }])}>
          <Plus /> {t('Add header')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t('Only for non-secret headers (e.g. a tenant ID). Tokens go in the field below.')}</p>
    </div>
  );
}
